import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseService';
import { proposalPdfService } from '../../../services/proposalPdfService';
import type { ProposalContent } from '../../../types/proposal-blocks';
import { ProposalTopbar } from './ProposalTopbar';
import { ProposalSidebarLeft } from './ProposalSidebarLeft';
import { ProposalSidebarRight } from './ProposalSidebarRight';
import { AcceptModal, type AcceptSubmitData } from './AcceptModal';
import { FloatingFeedback } from './FloatingFeedback';
import { BlockRenderer } from './blocks/BlockRenderer';
import './ProposalLayout.css';

interface PublicProposalData {
  id: string;
  title: string;
  content: ProposalContent;
  status: string;
  valid_until: string | null;
  accepted_at: string | null;
  accepted_by_name: string | null;
}

export function PublicProposalV2() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<PublicProposalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!slug) { setError('not_found'); setLoading(false); return; }
    (async () => {
      const { data: result, error: rpcError } = await supabase.rpc('get_public_proposal', { p_slug: slug });
      if (rpcError || !result || result.error) {
        setError(result?.error || 'error');
        setLoading(false);
        return;
      }
      setData(result as PublicProposalData);
      setLoading(false);
    })();
  }, [slug]);

  const navigate = useCallback((anchor: string) => {
    if (!anchor) return;
    setActiveSection(anchor);
    const el = document.getElementById(anchor);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  const handleAccept = async (submitData: AcceptSubmitData) => {
    if (!slug || !data) throw new Error('No slug');

    const { data: result, error: rpcError } = await supabase.rpc('accept_proposal', {
      p_slug: slug,
      p_name: submitData.name,
      p_email: submitData.email,
      p_signature_data: submitData.signatureData || null,
    });
    if (rpcError) throw new Error('Error de conexión.');
    if (result?.error) throw new Error(result.error);

    setData((d) => d ? { ...d, status: 'accepted' } : d);

    // PDF generation (non-blocking). Se sube al bucket signed-proposals y
    // se referencia desde proposals.signed_pdf_path para adjuntarlo en el
    // email de aceptación.
    let pdfPath: string | null = null;
    try {
      const pdfBlob = await proposalPdfService.generateSignedPdf({
        title: data.title,
        content: data.content as any,
        acceptedByName: submitData.name,
        acceptedByEmail: submitData.email,
        acceptedAt: new Date().toISOString(),
        signatureData: submitData.signatureData,
      });
      const fileName = `${slug}-${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage.from('signed-proposals').upload(fileName, pdfBlob, { contentType: 'application/pdf' });
      if (!uploadError) {
        await supabase.rpc('save_signed_pdf_path', { p_slug: slug, p_pdf_path: fileName });
        pdfPath = fileName;
      }
    } catch (e) {
      console.error('PDF generation error:', e);
    }

    // Emails de aceptación al cliente y al equipo interno. Si falla no
    // deshacemos la aceptación (la RPC accept_proposal ya persistió el
    // estado firmado).
    try {
      await supabase.functions.invoke('proposal-accepted', {
        body: {
          slug,
          proposal_title: data.title,
          accepted_by_name: submitData.name,
          accepted_by_email: submitData.email,
          accepted_at: new Date().toISOString(),
          signed_pdf_path: pdfPath,
        },
      });
    } catch (e) {
      console.error('proposal-accepted invoke failed (non-blocking):', e);
    }
  };

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (loading) {
    return (
      <div className="proposal-root min-h-screen flex items-center justify-center" data-theme={theme}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-proposal-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-proposal-fg2 text-sm">Cargando propuesta...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    const msgs: Record<string, { t: string; d: string }> = {
      not_found: { t: 'Propuesta no encontrada', d: 'El enlace no es válido o la propuesta no está disponible.' },
      expired: { t: 'Propuesta expirada', d: 'Esta propuesta ha superado su fecha de validez.' },
    };
    const m = msgs[error || 'error'] || { t: 'Error', d: 'No se pudo cargar la propuesta.' };
    return (
      <div className="proposal-root min-h-screen flex items-center justify-center" data-theme={theme}>
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-proposal-fg1 mb-2">{m.t}</h1>
          <p className="text-proposal-fg2 text-sm">{m.d}</p>
        </div>
      </div>
    );
  }

  const content = data.content;

  // Guard: if content has no blocks, show empty state
  if (!content?.blocks || !Array.isArray(content.blocks) || content.blocks.length === 0) {
    return (
      <div className="proposal-root min-h-screen flex items-center justify-center" data-theme={theme}>
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-proposal-fg1 mb-2">Propuesta en preparación</h1>
          <p className="text-proposal-fg2 text-sm">El contenido de esta propuesta aún se está generando. Vuelve en unos minutos.</p>
        </div>
      </div>
    );
  }

  // Build scroll-spy sections from blocks that have ids
  const scrollSpySections = content.blocks
    .filter((b): b is Extract<typeof b, { id: string }> => 'id' in b && typeof (b as any).id === 'string')
    .map((b) => ({ id: (b as any).id, label: (b as any).title || (b as any).name || (b as any).id }));

  // Build sidebar nav from content.navigation
  const sidebarGroups = (content.navigation?.groups || []).map((g) => ({
    title: g.label,
    items: g.items.map((item) => ({ id: item.anchor, label: item.label })),
  }));

  // Build topNavLinks from first items of each group
  const topNavLinks = (content.navigation?.groups || []).flatMap((g) =>
    g.items.slice(0, 2).map((item) => ({ id: item.anchor, label: item.label }))
  ).slice(0, 5);

  return (
    <div className="proposal-root" data-theme={theme}>
      <ProposalTopbar
        brandName={content.meta.brand}
        projectName={content.meta.project_name}
        topNavLinks={topNavLinks}
        currentPage={activeSection}
        theme={theme}
        onNavigate={navigate}
        onToggleTheme={toggleTheme}
        onOpenSearch={() => {}}
        onOpenModal={() => setModalOpen(true)}
      />

      <div className="flex pt-[56px] min-h-screen">
        <ProposalSidebarLeft
          groups={sidebarGroups}
          currentPage={activeSection}
          onNavigate={navigate}
          onOpenModal={() => setModalOpen(true)}
        />

        <main className="flex-1 min-w-0 px-6 md:px-12 py-12 pb-20 max-w-[760px]">
          {content.blocks.map((block, i) => (
            <BlockRenderer
              key={i}
              block={block}
              onNavigate={navigate}
              onOpenModal={() => setModalOpen(true)}
            />
          ))}

          {/* Final CTA */}
          <div className="text-center py-8 mt-8 border-t border-proposal-border">
            <p className="text-sm text-proposal-fg2 mb-4">¿Todo claro? Da el siguiente paso.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="bg-proposal-accent text-proposal-accent-text border-none rounded-p-md px-8 py-3 text-base font-semibold cursor-pointer transition-opacity hover:opacity-85 font-proposal-ui"
            >
              Aceptar y firmar →
            </button>
          </div>
        </main>

        <ProposalSidebarRight sections={scrollSpySections} />
      </div>

      {content.acceptance && (
        <AcceptModal
          config={{
            title: content.acceptance.title,
            subtitle: content.acceptance.subtitle || '',
            termsText: content.acceptance.conditions_text,
            requireSignature: content.acceptance.require_signature,
            reference: content.acceptance.reference,
            confirmationMessage: '¡Propuesta aceptada!',
            optionGroups: content.acceptance.option_groups?.map((g) => ({
              label: g.label,
              options: g.options.map((o) => ({
                value: o.value,
                label: o.label,
                description: o.description,
                recommended: o.recommended || false,
              })),
              required: true,
            })),
          }}
          isOpen={modalOpen}
          proposalStatus={data.status}
          onClose={() => setModalOpen(false)}
          onSubmit={handleAccept}
        />
      )}

      <FloatingFeedback />
    </div>
  );
}
